/**
 * Configuração e Utilitários de Tipografia Compatível com Serviços de Email e Estrutura de Templates
 * Editor de Templates de Email - Seguros Virtual 2026 (Feedback #507)
 */

export interface EmailSafeFontOption {
  id: string;
  label: string;
  shortLabel: string;
  badge: string;
  value: string;
  category: 'sans-serif' | 'serif' | 'monospace';
}

/**
 * Lista oficial de tipos de letra 100% compatíveis com todos os principais serviços de email
 * (Gmail, Outlook Desktop/Web, Apple Mail, Yahoo Mail, Thunderbird, Roundcube).
 */
export const EMAIL_SAFE_FONTS: EmailSafeFontOption[] = [
  {
    id: 'arial',
    label: 'Arial',
    shortLabel: 'Arial',
    badge: 'Padrão Universal',
    value: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
    category: 'sans-serif',
  },
  {
    id: 'verdana',
    label: 'Verdana',
    shortLabel: 'Verdana',
    badge: 'Alta Legibilidade',
    value: 'Verdana, Geneva, DejaVu Sans, sans-serif',
    category: 'sans-serif',
  },
  {
    id: 'tahoma',
    label: 'Tahoma',
    shortLabel: 'Tahoma',
    badge: 'Compacta & Moderna',
    value: 'Tahoma, Verdana, Segoe, sans-serif',
    category: 'sans-serif',
  },
  {
    id: 'trebuchet',
    label: 'Trebuchet MS',
    shortLabel: 'Trebuchet MS',
    badge: 'Corporativa',
    value: "'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', 'Lucida Sans', sans-serif",
    category: 'sans-serif',
  },
  {
    id: 'segoe',
    label: 'Segoe UI',
    shortLabel: 'Segoe UI',
    badge: 'Windows / Outlook',
    value: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    category: 'sans-serif',
  },
  {
    id: 'helvetica',
    label: 'Helvetica Neue',
    shortLabel: 'Helvetica',
    badge: 'Apple / Web',
    value: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    category: 'sans-serif',
  },
  {
    id: 'georgia',
    label: 'Georgia',
    shortLabel: 'Georgia',
    badge: 'Clássica Serifada',
    value: "Georgia, Times, 'Times New Roman', serif",
    category: 'serif',
  },
  {
    id: 'times',
    label: 'Times New Roman',
    shortLabel: 'Times New Roman',
    badge: 'Formal',
    value: "'Times New Roman', Times, Baskerville, Georgia, serif",
    category: 'serif',
  },
  {
    id: 'courier',
    label: 'Courier New',
    shortLabel: 'Courier New',
    badge: 'Monoespaçada',
    value: "'Courier New', Courier, 'Lucida Sans Typewriter', monospace",
    category: 'monospace',
  },
];

export const DEFAULT_EMAIL_SAFE_FONT_FAMILY = "Arial, 'Helvetica Neue', Helvetica, sans-serif";

/**
 * Mantido por compatibilidade de imports; aponta para a fonte padrão compatível com todos os clientes de email.
 */
export const NASALIZATION_FONT_FAMILY = DEFAULT_EMAIL_SAFE_FONT_FAMILY;

export const NASALIZATION_FONT_URL = 'https://segurosvirtual.pt/assets/fonts/nasalization.css';

export const NASALIZATION_CUSTOM_CSS = `
html, body, #u_body {
  font-family: ${DEFAULT_EMAIL_SAFE_FONT_FAMILY};
}
strong, b {
  font-weight: 700;
}
em, i {
  font-style: italic;
}
u {
  text-decoration: underline;
}
`;

export const NASALIZATION_EMAIL_CSS = `
body, #u_body, table, tbody, tr, td, th, p, a, li, ul, ol, div, span, h1, h2, h3, h4, h5, h6, button, strong, em, b, i, u, blockquote {
  font-family: ${DEFAULT_EMAIL_SAFE_FONT_FAMILY};
}
strong, b {
  font-weight: 700;
}
em, i {
  font-style: italic;
}
`;

/**
 * Verifica se uma declaração font-family corresponde a uma fonte compatível com serviços de email
 * e devolve a font-stack normalizada, ou null se for uma fonte não suportada (como Nasalization).
 */
export function resolveCompatibleEmailFontStack(rawFontValue?: string | null, fallbackFont?: string): string {
  const defaultStack = fallbackFont || DEFAULT_EMAIL_SAFE_FONT_FAMILY;
  if (!rawFontValue || typeof rawFontValue !== 'string') return defaultStack;

  const cleaned = rawFontValue.replace(/!important/gi, '').trim();
  if (!cleaned || /nasalization/i.test(cleaned)) {
    return defaultStack;
  }

  const primaryFont = (cleaned.split(',')[0] || '').replace(/['"]/g, '').trim().toLowerCase();
  const lower = cleaned.toLowerCase();

  if (primaryFont.includes('tahoma')) return EMAIL_SAFE_FONTS[2].value;
  if (primaryFont.includes('segoe')) return EMAIL_SAFE_FONTS[4].value;
  if (primaryFont.includes('verdana')) return EMAIL_SAFE_FONTS[1].value;
  if (primaryFont.includes('trebuchet')) return EMAIL_SAFE_FONTS[3].value;
  if (primaryFont.includes('times')) return EMAIL_SAFE_FONTS[7].value;
  if (primaryFont.includes('georgia')) return EMAIL_SAFE_FONTS[6].value;
  if (primaryFont.includes('courier') || primaryFont.includes('monospace')) return EMAIL_SAFE_FONTS[8].value;
  if (primaryFont.includes('helvetica')) return EMAIL_SAFE_FONTS[5].value;
  if (primaryFont.includes('arial')) return EMAIL_SAFE_FONTS[0].value;

  if (lower.includes('tahoma') && !lower.includes('segoe')) return EMAIL_SAFE_FONTS[2].value;
  if (lower.includes('segoe')) return EMAIL_SAFE_FONTS[4].value;
  if (lower.includes('verdana')) return EMAIL_SAFE_FONTS[1].value;
  if (lower.includes('trebuchet')) return EMAIL_SAFE_FONTS[3].value;
  if (lower.includes('times') && !lower.startsWith('georgia')) return EMAIL_SAFE_FONTS[7].value;
  if (lower.includes('georgia')) return EMAIL_SAFE_FONTS[6].value;
  if (lower.includes('courier') || lower.includes('monospace')) return EMAIL_SAFE_FONTS[8].value;
  if (lower.includes('helvetica') && !lower.startsWith('arial')) return EMAIL_SAFE_FONTS[5].value;
  if (lower.includes('arial') || lower.includes('sans-serif')) return EMAIL_SAFE_FONTS[0].value;

  return defaultStack;
}

/**
 * Verifica se uma imagem é um banner de cabeçalho ou rodapé que deve ocupar 100% da largura do template
 */
export function isFullBleedBannerImageElement(
  img: HTMLImageElement,
  indexInTemplate: number,
  totalImagesInTemplate?: number,
): boolean {
  if (!img) return false;
  if (img.getAttribute('data-sv-fullbleed-banner') === 'false') return false;
  if (img.getAttribute('data-sv-fullbleed-banner') === 'true') return true;

  const rawSrc = (img.getAttribute('src') || '').toLowerCase();
  // Ignorar conteúdo de strings Base64 na pesquisa textual por palavras-chave de ficheiro
  const src = rawSrc.startsWith('data:') ? '' : rawSrc;
  const alt = (img.getAttribute('alt') || '').toLowerCase();
  const styleAttr = (img.getAttribute('style') || '').toLowerCase();
  const widthAttr = parseInt(img.getAttribute('width') || '0', 10);

  // Nunca esticar logótipos pequenos, ícones de lista, botões ou assinaturas
  if (
    src.includes('logotexto') ||
    src.includes('logologin') ||
    src.includes('favicon') ||
    src.includes('icon') ||
    src.includes('botao') ||
    src.includes('assinatura') ||
    alt.includes('assinatura') ||
    alt.includes('aceder à minha área') ||
    alt.includes('ver preço') ||
    (widthAttr > 0 && widthAttr < 240) ||
    /max-width:\s*(?:[1-9]\d|1\d\d|2[0-3]\d)px/i.test(styleAttr) ||
    /max-height:\s*(?:[1-5]\d)px/i.test(styleAttr)
  ) {
    return false;
  }

  // Cabeçalho e rodapé oficiais da Seguros Virtual ou banners exportados do Unlayer
  if (
    src.includes('cabecalho') ||
    src.includes('rodape') ||
    src.includes('gradient-banner') ||
    src.includes('cabe') ||
    src.includes('rodap') ||
    alt.includes('cabeçalho') ||
    alt.includes('cabecalho') ||
    alt.includes('rodapé') ||
    alt.includes('rodape') ||
    alt.includes('banner') ||
    Boolean(img.closest('.email-header-banner, .email-footer-banner'))
  ) {
    return true;
  }

  // Se for a primeira (cabeçalho) ou última (rodapé) imagem do template (e não foi excluída como ícone/botão/assinatura acima),
  // deve ocupar 100% da largura do template mesmo que o Unlayer a tenha exportado com width="412" / max-width: 412px.
  const isFirstOrLastBannerPosition =
    indexInTemplate === 0 ||
    (typeof totalImagesInTemplate === 'number' &&
      totalImagesInTemplate > 1 &&
      indexInTemplate === totalImagesInTemplate - 1);

  if (isFirstOrLastBannerPosition) {
    return true;
  }

  return false;
}

/**
 * Normaliza a estrutura de templates (incluindo Unlayer .u-row 500px -> 600px e tabelas MSO Outlook) e garante que o
 * cabeçalho, corpo, assinatura e rodapé ficam alinhados na mesma coluna central de 600px sem scroll horizontal.
 */
export function normalizeEmailHeaderAndStructureHtml(
  rawHtml: string,
  options: { collapseEmptyParagraphs?: boolean } = {},
): string {
  if (!rawHtml || typeof rawHtml !== 'string') return '';

  // 1. Normalizar larguras de estrutura Unlayer e comentários condicionais MSO (Outlook) de 500px para 600px
  let html = rawHtml
    .replace(/(\.u-row\s*\{\s*width:\s*)500px(\s*!important\s*;\s*\})/gi, '$1600px$2')
    .replace(/(\.u-row\s+\.u-col-100\s*\{\s*width:\s*)500px(\s*!important\s*;\s*\})/gi, '$1600px$2')
    .replace(/(\.u-row\s+\.u-col-50\s*\{\s*width:\s*)250px(\s*!important\s*;\s*\})/gi, '$1300px$2')
    .replace(/(\.u-row\s+\.u-col-33\s*\{\s*width:\s*)166\.67px(\s*!important\s*;\s*\})/gi, '$1200px$2')
    .replace(/(\.u-row\s+\.u-col-67\s*\{\s*width:\s*)333\.33px(\s*!important\s*;\s*\})/gi, '$1400px$2')
    .replace(/<!--\[if\s*\(mso\)\|\(IE\)\]>([\s\S]*?)<!\[endif\]-->/gi, (_m, msoInner: string) => {
      const updatedMso = msoInner
        .replace(/\bwidth=(["'])500\1/gi, 'width=$1600$1')
        .replace(/width\s*:\s*500px/gi, 'width: 600px')
        .replace(/\bwidth=(["'])250\1/gi, 'width=$1300$1')
        .replace(/width\s*:\s*250px/gi, 'width: 300px');
      return `<!--[if (mso)|(IE)]>${updatedMso}<![endif]-->`;
    });

  if (typeof DOMParser === 'undefined') {
    return html;
  }

  try {
    const isFullDoc = /<!doctype|<html\b|<body\b/i.test(html);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    if (!doc || !doc.body) return html;

    normalizeEmailHeaderAndStructureOnElement(doc.body, options);

    if (isFullDoc) {
      const doctype = /^<!doctype[^>]*>/i.exec(html)?.[0] || '<!DOCTYPE html>';
      return `${doctype}\n${doc.documentElement.outerHTML}`;
    }
    return doc.body.innerHTML;
  } catch (_) {
    return html;
  }
}

/**
 * Aplica diretamente no DOM (Document/HTMLElement) a normalização de largura (600px), alinhamento na coluna central
 * (cabeçalho, texto, assinatura e rodapé), remoção de linhas em branco excessivas e eliminação de scroll horizontal
 * no Outlook e restantes clientes de email (Feedback #590 / #593).
 */
export function normalizeEmailHeaderAndStructureOnElement(
  rootEl: HTMLElement,
  options: { collapseEmptyParagraphs?: boolean } = {},
): void {
  if (!rootEl) return;

  // 0. Ler configurações autónomas do template se definidas pelo utilizador no editor (data-sv-*)
  const configHolder =
    (rootEl.hasAttribute('data-sv-border-radius') ||
    rootEl.hasAttribute('data-sv-margin-top') ||
    rootEl.hasAttribute('data-sv-body-bg') ||
    rootEl.hasAttribute('data-sv-outer-bg')
      ? rootEl
      : (rootEl.querySelector(
          '[data-sv-template-root], [data-sv-border-radius], [data-sv-margin-top], [data-sv-body-bg], [data-sv-outer-bg]',
        ) as HTMLElement | null)) || null;

  const customBorderRadius = configHolder?.getAttribute('data-sv-border-radius') || '0px';
  const customMarginTop = configHolder?.getAttribute('data-sv-margin-top') || '0px';
  const customMarginBottom = configHolder?.getAttribute('data-sv-margin-bottom') || '0px';
  const customBodyBg = configHolder?.getAttribute('data-sv-body-bg') || '#ffffff';
  const customOuterBg = configHolder?.getAttribute('data-sv-outer-bg') || customBodyBg;
  const customTextPadX = configHolder?.getAttribute('data-sv-text-padding-x') || '';
  const customTextPadY = configHolder?.getAttribute('data-sv-text-padding-y') || '';

  const isLegacyGreyBg = (bgVal: string): boolean => {
    const lower = (bgVal || '').toLowerCase().replace(/\s+/g, '');
    return (
      lower.includes('#ecf0f1') ||
      lower.includes('#f1f5f9') ||
      lower.includes('#f4f4f7') ||
      lower.includes('#f4f7f9') ||
      lower.includes('#e7e7e7') ||
      lower.includes('rgb(236,240,241)') ||
      lower.includes('rgb(241,245,249)') ||
      lower.includes('rgb(244,244,247)') ||
      lower.includes('rgb(244,247,249)') ||
      lower.includes('rgb(231,231,231)')
    );
  };

  // 0.0 Unificar blocos órfãos (Assinatura Hugo Capela, Rodapé "Segue-nos", Cabeçalho, etc.) que tenham sido inseridos
  // fora da tabela principal (#u_body / .nl-container), garantindo que ficam todos dentro da mesma estrutura central (Feedback #593)
  const mainOuterTable = rootEl.querySelector(
    ':scope > table#u_body, :scope > table.nl-container, :scope > table.u-body, :scope > table[width="100%"]',
  ) as HTMLElement | null;
  if (mainOuterTable) {
    const mainTd = mainOuterTable.querySelector(':scope > tbody > tr > td, :scope > tr > td') as HTMLElement | null;
    if (mainTd) {
      const directChildren = Array.from(rootEl.children) as HTMLElement[];
      directChildren.forEach(child => {
        if (child === mainOuterTable) return;
        const tag = child.tagName.toUpperCase();
        if (tag === 'STYLE' || tag === 'LINK' || tag === 'SCRIPT' || tag === 'META') return;
        if (child.classList.contains('email-header-banner') && mainTd.firstChild) {
          mainTd.insertBefore(child, mainTd.firstChild);
        } else {
          mainTd.appendChild(child);
        }
      });
    }
  }

  // 0.1 Normalizar fundo do próprio rootEl (ex: <body>) e tabelas de fundo exterior (#u_body, .nl-container, .u-body, width="100%")
  if (rootEl.tagName === 'BODY') {
    rootEl.style.setProperty('margin', '0px', 'important');
    rootEl.style.setProperty('padding', '0px', 'important');
    rootEl.style.setProperty('overflow-x', 'hidden', 'important');
    rootEl.style.setProperty('width', '100%', 'important');
    if (
      !configHolder?.hasAttribute('data-sv-outer-bg') &&
      isLegacyGreyBg(rootEl.style.backgroundColor || rootEl.getAttribute('bgcolor') || '')
    ) {
      rootEl.style.setProperty('background-color', customOuterBg, 'important');
    } else if (configHolder?.hasAttribute('data-sv-outer-bg')) {
      rootEl.style.setProperty('background-color', customOuterBg, 'important');
    } else if (!rootEl.style.backgroundColor) {
      rootEl.style.setProperty('background-color', customOuterBg, 'important');
    }
  }

  const outerFullTables = Array.from(
    rootEl.querySelectorAll('table#u_body, table.nl-container, table.u-body, div.u-body, table[width="100%"]'),
  ) as HTMLElement[];
  outerFullTables.forEach(tbl => {
    // Não alterar tabelas internas de conteúdo que tenham fundo escuro institucional
    const bg = tbl.style.backgroundColor || tbl.getAttribute('bgcolor') || '';
    if (
      isLegacyGreyBg(bg) ||
      tbl.id === 'u_body' ||
      tbl.classList.contains('nl-container') ||
      tbl.classList.contains('u-body')
    ) {
      tbl.style.setProperty('background-color', customOuterBg, 'important');
      if (tbl.hasAttribute('bgcolor')) {
        tbl.setAttribute('bgcolor', customOuterBg);
      }
      tbl.style.setProperty('margin-top', '0px', 'important');
      tbl.style.setProperty('padding-top', '0px', 'important');
      tbl.style.setProperty('padding-left', '0px', 'important');
      tbl.style.setProperty('padding-right', '0px', 'important');
    }
    // Se for a tabela exterior de 100% que envolve a tabela de 600px, remover o padding superior/lateral da sua célula
    const inner600 = tbl.querySelector(
      'table[width="600"], table[style*="max-width: 600px"], table[style*="max-width:600px"], .u-row',
    );
    if (inner600) {
      const directTd = tbl.querySelector(':scope > tbody > tr > td, :scope > tr > td') as HTMLElement | null;
      if (directTd && directTd.contains(inner600)) {
        directTd.style.setProperty('padding-top', customMarginTop, 'important');
        directTd.style.setProperty('padding-bottom', customMarginBottom, 'important');
        directTd.style.setProperty('padding-left', '0px', 'important');
        directTd.style.setProperty('padding-right', '0px', 'important');
        directTd.setAttribute('align', 'center');
      }
    }
  });

  // 0.2 Normalizar contentores principais de 600px / 620px e blocos de secção (cantos retos 0px, centrados e box-sizing: border-box)
  const allContainers = Array.from(rootEl.querySelectorAll('div, table')) as HTMLElement[];
  allContainers.forEach(el => {
    const rawStyle = (el.getAttribute('style') || '').toLowerCase();
    const isMain600Card =
      el.hasAttribute('data-sv-template-root') ||
      rawStyle.includes('max-width: 600px') ||
      rawStyle.includes('max-width:600px') ||
      rawStyle.includes('max-width: 620px') ||
      rawStyle.includes('max-width:620px') ||
      el.getAttribute('width') === '600';

    if (isMain600Card && !el.closest('[data-sv-builder-block="highlight-box"]')) {
      const blockRadius = el.getAttribute('data-sv-block-radius');
      const targetRadius = blockRadius || customBorderRadius || '0px';
      el.style.setProperty('border-radius', targetRadius, 'important');
      el.style.setProperty('box-sizing', 'border-box', 'important');
      if (targetRadius === '0px' || targetRadius === '0') {
        el.style.setProperty('box-shadow', 'none', 'important');
      }
      // Remover margin: 24px auto no contentor raiz do template e garantir alinhamento central
      if (el.parentElement === rootEl || el.hasAttribute('data-sv-template-root')) {
        el.style.setProperty('margin-top', customMarginTop, 'important');
        el.style.setProperty('margin-bottom', customMarginBottom, 'important');
      }
      el.style.setProperty('margin-left', 'auto', 'important');
      el.style.setProperty('margin-right', 'auto', 'important');
    }
  });

  // 0.3 Garantir que quaisquer blocos construtores (.email-header-banner, .email-footer-banner, [data-sv-builder-block], [data-sv-block-type])
  // que não sejam highlight-box interna têm max-width: 600px, margin: 0 auto e box-sizing: border-box (evita desalinhamento à esquerda e scroll horizontal)
  const builderBlocks = Array.from(
    rootEl.querySelectorAll(
      '.email-header-banner, .email-footer-banner, [data-sv-builder-block], [data-sv-block-type]',
    ),
  ) as HTMLElement[];
  builderBlocks.forEach(block => {
    const bType = block.getAttribute('data-sv-builder-block') || block.getAttribute('data-sv-block-type') || '';
    if (bType === 'highlight-box') return;
    block.style.setProperty('box-sizing', 'border-box', 'important');
    if (!block.closest('.u-row')) {
      block.style.setProperty('max-width', '600px', 'important');
      block.style.setProperty('width', '100%', 'important');
      block.style.setProperty('margin-left', 'auto', 'important');
      block.style.setProperty('margin-right', 'auto', 'important');
      if (!block.style.backgroundColor || block.style.backgroundColor === 'transparent') {
        block.style.setProperty('background-color', customBodyBg);
      }
    }
  });

  // 1. Expandir .u-row de 500px para 600px (100% da largura do cartão do template) e cantos retos
  const uRowContainers = Array.from(rootEl.querySelectorAll('.u-row-container')) as HTMLElement[];
  uRowContainers.forEach((rowCont, idx) => {
    const bg = rowCont.style.backgroundColor || '';
    if (isLegacyGreyBg(bg)) {
      rowCont.style.setProperty('background-color', customOuterBg, 'important');
    }
    if (idx === 0) {
      rowCont.style.setProperty('padding-top', customMarginTop, 'important');
      rowCont.style.setProperty('margin-top', '0px', 'important');
    }
  });

  const uRows = Array.from(rootEl.querySelectorAll('.u-row')) as HTMLElement[];
  uRows.forEach(row => {
    row.style.setProperty('max-width', '600px', 'important');
    row.style.setProperty('width', '100%', 'important');
    row.style.setProperty('margin', '0 auto', 'important');
    if (!row.hasAttribute('data-sv-block-radius')) {
      row.style.setProperty('border-radius', '0px', 'important');
    }
  });

  const uCol100s = Array.from(rootEl.querySelectorAll('.u-col-100')) as HTMLElement[];
  uCol100s.forEach(col => {
    col.style.setProperty('min-width', '100%', 'important');
    col.style.setProperty('max-width', '100%', 'important');
    col.style.setProperty('width', '100%', 'important');
  });

  const uCol50s = Array.from(rootEl.querySelectorAll('.u-col-50')) as HTMLElement[];
  uCol50s.forEach(col => {
    col.style.setProperty('min-width', '50%', 'important');
    col.style.setProperty('max-width', '50%', 'important');
    col.style.setProperty('width', '50%', 'important');
  });

  // 2. Auto-reparar imagens expiradas do Unlayer / placeholders quebrados e normalizar cabeçalho/rodapé full-bleed e assinatura
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://segurosvirtual.pt';
  const allImages = Array.from(rootEl.querySelectorAll('img')) as HTMLImageElement[];
  allImages.forEach((img, idx) => {
    const rawSrc = (img.getAttribute('src') || '').trim();
    const lowerSrc = rawSrc.toLowerCase();
    const lowerAlt = (img.getAttribute('alt') || '').toLowerCase();

    // Auto-reparar URLs temporários/expirados do Unlayer (qualquer projects/\d+) ou placeholder antigo
    if (
      lowerSrc.includes('assets.unlayer.com') ||
      lowerSrc.includes('cdn.tools.unlayer.com') ||
      lowerSrc.endsWith('/assets/images/logo.png') ||
      !rawSrc
    ) {
      if (lowerSrc.endsWith('/assets/images/logo.png')) {
        img.setAttribute('src', `${origin}/assets/images/logo/logotexto.png`);
      } else if (
        lowerSrc.includes('cabe') ||
        lowerSrc.includes('header') ||
        lowerAlt.includes('cabe') ||
        lowerAlt.includes('header')
      ) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/cabecalho.png`);
      } else if (
        lowerSrc.includes('rodap') ||
        lowerSrc.includes('footer') ||
        lowerAlt.includes('rodap') ||
        lowerAlt.includes('footer')
      ) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/rodape.png`);
      } else if (
        (lowerSrc.includes('30') && lowerSrc.includes('seg')) ||
        (lowerAlt.includes('30') && lowerAlt.includes('seg'))
      ) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/botao_30_segundos.png`);
      } else if (
        (lowerSrc.includes('area') && lowerSrc.includes('cliente')) ||
        lowerAlt.includes('área de cliente') ||
        lowerAlt.includes('area de cliente')
      ) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/botao_area_cliente.png`);
      } else if (lowerSrc.includes('assinatura') || lowerSrc.includes('hugo') || lowerAlt.includes('assinatura')) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/assinatura_hugo_capela.png`);
      } else if (idx === 0) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/cabecalho.png`);
        if (!img.getAttribute('alt')) img.setAttribute('alt', 'Cabeçalho Seguros Virtual');
      } else if (allImages.length > 1 && idx === allImages.length - 1) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/rodape.png`);
        if (!img.getAttribute('alt')) img.setAttribute('alt', 'Rodapé Seguros Virtual');
      } else if (!rawSrc || lowerSrc.includes('cdn.tools.unlayer.com')) {
        img.setAttribute('src', `${origin}/assets/EmailsMarketing/rodape.png`);
      }
    }

    const updatedSrcLower = (img.getAttribute('src') || '').toLowerCase();
    const isSignatureImg =
      updatedSrcLower.includes('assinatura') ||
      lowerAlt.includes('assinatura') ||
      img.getAttribute('data-sv-banner-role') === 'signature';

    if (isSignatureImg) {
      img.setAttribute('data-sv-banner-role', 'signature');
      img.style.setProperty('max-width', '100%', 'important');
      img.style.setProperty('height', 'auto', 'important');
      img.style.setProperty('display', 'block', 'important');
      img.style.setProperty('border', '0', 'important');
      img.style.setProperty('margin', '0 auto 0 0', 'important');

      const sigWrapper = img.closest(
        '[data-sv-builder-block="image"], [data-sv-block-type="image"]',
      ) as HTMLElement | null;
      if (sigWrapper && !sigWrapper.closest('.u-row')) {
        sigWrapper.style.setProperty('max-width', '600px', 'important');
        sigWrapper.style.setProperty('width', '100%', 'important');
        sigWrapper.style.setProperty('margin-left', 'auto', 'important');
        sigWrapper.style.setProperty('margin-right', 'auto', 'important');
        sigWrapper.style.setProperty('margin-top', '0px', 'important');
        sigWrapper.style.setProperty('margin-bottom', '0px', 'important');
        sigWrapper.style.setProperty('padding', '4px 28px 16px 28px', 'important');
        sigWrapper.style.setProperty('text-align', 'left', 'important');
        sigWrapper.style.setProperty('box-sizing', 'border-box', 'important');
        sigWrapper.style.setProperty('background-color', customBodyBg);
      }
      return;
    }

    if (!isFullBleedBannerImageElement(img, idx, allImages.length)) return;

    img.setAttribute('data-sv-fullbleed-banner', 'true');
    if (idx === 0 || updatedSrcLower.includes('cabecalho')) {
      img.setAttribute('data-sv-banner-role', 'header');
    } else if ((allImages.length > 1 && idx === allImages.length - 1) || updatedSrcLower.includes('rodape')) {
      img.setAttribute('data-sv-banner-role', 'footer');
    }
    img.setAttribute('width', '600');
    img.style.setProperty('width', '100%', 'important');
    img.style.setProperty('max-width', '600px', 'important');
    img.style.setProperty('height', 'auto', 'important');
    img.style.setProperty('display', 'block', 'important');
    img.style.setProperty('margin', '0 auto', 'important');
    img.style.setProperty('padding', '0', 'important');
    img.style.setProperty('border', '0', 'important');
    img.style.setProperty('border-radius', '0', 'important');

    // Subir na árvore DOM até à linha (.u-row-container ou 1º nível abaixo do rootEl) para zerar paddings e centrar a 600px
    let curr: HTMLElement | null = img.parentElement;
    while (curr && curr !== rootEl) {
      if (
        curr === mainOuterTable ||
        curr.id === 'u_body' ||
        curr.classList.contains('nl-container') ||
        curr.classList.contains('u-body')
      ) {
        break;
      }
      const tag = curr.tagName.toLowerCase();
      const cls = curr.className || '';

      // Se chegarmos a um contentor global que envolve o template inteiro (com múltiplos blocos de texto + imagem),
      // não zeramos o padding se ele tiver texto direto, mas zeramos células/tabelas/linhas do cabeçalho/rodapé.
      const hasParagraphsInside = curr.querySelectorAll('p, ul, ol').length > 0;
      const isRowOrCell =
        tag === 'td' ||
        tag === 'tr' ||
        tag === 'tbody' ||
        tag === 'table' ||
        cls.includes('u-col') ||
        cls.includes('u-row') ||
        cls.includes('v-container-padding-padding') ||
        cls.includes('email-header-banner') ||
        cls.includes('email-footer-banner') ||
        curr.getAttribute('data-sv-block-type') === 'image' ||
        curr.getAttribute('data-sv-builder-block') === 'image';

      if (isRowOrCell && !hasParagraphsInside) {
        curr.style.setProperty('padding', '0px', 'important');
        curr.style.setProperty('padding-top', '0px', 'important');
        curr.style.setProperty('padding-right', '0px', 'important');
        curr.style.setProperty('padding-bottom', '0px', 'important');
        curr.style.setProperty('padding-left', '0px', 'important');
        if (
          tag === 'table' ||
          tag === 'td' ||
          cls.includes('u-col') ||
          cls.includes('u-row') ||
          cls.includes('email-header-banner') ||
          cls.includes('email-footer-banner') ||
          curr.getAttribute('data-sv-block-type') === 'image' ||
          curr.getAttribute('data-sv-builder-block') === 'image'
        ) {
          curr.style.setProperty('width', '100%', 'important');
          curr.style.setProperty('max-width', '600px', 'important');
          curr.style.setProperty('margin-left', 'auto', 'important');
          curr.style.setProperty('margin-right', 'auto', 'important');
          curr.style.setProperty('text-align', 'center', 'important');
          curr.style.setProperty('box-sizing', 'border-box', 'important');
        }
      }

      if (cls.includes('u-row-container')) {
        if (!hasParagraphsInside) {
          curr.style.setProperty('padding', '0px', 'important');
        }
        break;
      }
      curr = curr.parentElement;
    }
  });

  // 3. Garantir margens internas (padding horizontal e vertical) nas células de texto quando .u-row passa de 500px para 600px
  const textPaddingCells = Array.from(
    rootEl.querySelectorAll(
      'table[id^="u_content_text_"] td.v-container-padding-padding, table[id^="u_content_heading_"] td.v-container-padding-padding, td.v-container-padding-padding',
    ),
  ) as HTMLElement[];

  let firstTextCellNormalized = false;
  textPaddingCells.forEach(cell => {
    if (cell.querySelector('img[data-sv-fullbleed-banner="true"]')) return;
    const hasTextContent =
      cell.querySelectorAll('p, h1, h2, h3, h4, ul, ol, blockquote').length > 0 ||
      Boolean((cell.textContent || '').trim());
    if (!hasTextContent) return;

    // Se a célula pertence a uma tabela de imagem ou botão sem parágrafos, não forçar padding de texto
    const parentContentTable = cell.closest('table[id^="u_content_"]') as HTMLElement | null;
    if (
      parentContentTable &&
      (parentContentTable.id.startsWith('u_content_image_') || parentContentTable.id.startsWith('u_content_divider_'))
    ) {
      return;
    }

    const padLeft = parseFloat(cell.style.paddingLeft || '0');
    const padRight = parseFloat(cell.style.paddingRight || '0');
    const padTop = parseFloat(cell.style.paddingTop || '0');

    if (customTextPadX) {
      cell.style.setProperty('padding-left', customTextPadX, 'important');
      cell.style.setProperty('padding-right', customTextPadX, 'important');
    } else {
      if (isNaN(padLeft) || padLeft < 16) {
        cell.style.setProperty('padding-left', '28px', 'important');
      }
      if (isNaN(padRight) || padRight < 16) {
        cell.style.setProperty('padding-right', '28px', 'important');
      }
    }
    if (!firstTextCellNormalized) {
      firstTextCellNormalized = true;
      if (customTextPadY) {
        cell.style.setProperty('padding-top', customTextPadY, 'important');
      } else if (isNaN(padTop) || padTop < 16) {
        cell.style.setProperty('padding-top', '22px', 'important');
      }
    }
  });

  // 4. Compactar parágrafos vazios (<p><br></p>, <p>&nbsp;</p>) e normalizar margens de <p> para evitar espaçamento exagerado no Outlook
  const isParagraphEffectivelyEmpty = (p: HTMLElement): boolean => {
    if (p.querySelector('img, a, button, input, table, svg, hr')) return false;
    const text = (p.textContent || '').replace(/\u00a0/g, '').trim();
    return text.length === 0;
  };

  const allParagraphs = Array.from(rootEl.querySelectorAll('p')) as HTMLElement[];
  allParagraphs.forEach(p => {
    if (isParagraphEffectivelyEmpty(p)) {
      if (options?.collapseEmptyParagraphs) {
        p.remove();
        return;
      }
      // Mesmo no editor interativo, remover parágrafos vazios consecutivos ou entre blocos de texto
      const prevEl = p.previousElementSibling as HTMLElement | null;
      const nextEl = p.nextElementSibling as HTMLElement | null;
      if (
        (prevEl && prevEl.tagName === 'P' && isParagraphEffectivelyEmpty(prevEl)) ||
        (prevEl && prevEl.tagName === 'P' && nextEl && nextEl.tagName === 'P')
      ) {
        p.remove();
        return;
      }
    }
  });

  const remainingParagraphs = Array.from(rootEl.querySelectorAll('p')) as HTMLElement[];
  remainingParagraphs.forEach(p => {
    p.style.setProperty('margin-top', '0px');
    const existingMb = p.style.marginBottom;
    if (!existingMb || parseFloat(existingMb) > 12) {
      p.style.setProperty('margin-bottom', '8px');
    }
  });
}

/**
 * Remove blocos <style> e <link> globais antes de inserir HTML de templates diretamente no DOM da
 * aplicação (editores contenteditable).
 */
export function stripNasalizationGlobals(html: string): string {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<link[^>]*>/gi, '');
}

function buildCssDataUrl(css: string): string {
  try {
    if (typeof btoa === 'function') {
      return `data:text/css;charset=utf-8;base64,${btoa(unescape(encodeURIComponent(css)))}`;
    }
  } catch (_) {}
  return `data:text/css;charset=utf-8,${encodeURIComponent(css)}`;
}

export const NASALIZATION_DATA_CSS_URL = buildCssDataUrl(NASALIZATION_CUSTOM_CSS);

export function getNasalizationFontConfig(_origin?: string) {
  return {
    label: 'Arial (Compatível Email)',
    value: DEFAULT_EMAIL_SAFE_FONT_FAMILY,
    url: '',
    defaultFont: true,
  };
}

/**
 * Normaliza estilos inline garantindo fontes compatíveis com serviços de email (substituindo Nasalization
 * por Arial ou mantendo a fonte email-safe escolhida pelo utilizador) e preservando cores/tamanhos.
 */
export function enforceNasalizationInInlineStyles(html: string, preferredFontFamily?: string): string {
  if (!html || typeof html !== 'string') return '';
  const targetDefaultFont = preferredFontFamily || DEFAULT_EMAIL_SAFE_FONT_FAMILY;

  // 1. Normalizar font-family dentro de blocos <style> (removendo @font-face de nasalization)
  let result = html.replace(/<style([^>]*)>([\s\S]*?)<\/style>/gi, (_fullMatch, attrs: string, cssContent: string) => {
    const cleanedCss = cssContent
      .replace(/@font-face\s*\{[^}]*nasalization[^}]*\}/gi, '')
      .replace(/font-family\s*:\s*([^;!}]+)(\s*!important)?/gi, (_m, fontVal) => {
        const resolved = resolveCompatibleEmailFontStack(fontVal, targetDefaultFont);
        return `font-family: ${resolved}`;
      });
    return `<style${attrs}>${cleanedCss}</style>`;
  });

  // 2. Converter tags <font> para <span> preservando cor, tamanho, face e estilo inline
  const fontSizeMap: Record<string, string> = {
    '1': '10px',
    '2': '13px',
    '3': '15px',
    '4': '18px',
    '5': '24px',
    '6': '32px',
    '7': '48px',
  };
  for (let pass = 0; pass < 6 && /<font\b/i.test(result); pass++) {
    const prev = result;
    result = result.replace(/<font\b([^>]*)>([\s\S]*?)<\/font>/gi, (_m, rawAttrs: string, inner: string) => {
      const attrs = rawAttrs || '';
      const colorMatch = attrs.match(/\bcolor\s*=\s*(["'])([^"']*)\1/i);
      const sizeMatch = attrs.match(/\bsize\s*=\s*(["'])([^"']*)\1/i);
      const faceMatch = attrs.match(/\bface\s*=\s*(["'])([^"']*)\1/i);
      const styleMatch = attrs.match(/\bstyle\s*=\s*(["'])([\s\S]*?)\1/i);
      const styleParts: string[] = [];
      if (styleMatch && styleMatch[2]) {
        const cleanExisting = styleMatch[2]
          .replace(/&quot;|&#39;|&apos;/gi, "'")
          .replace(/"/g, "'")
          .trim()
          .replace(/;+$/, '');
        if (cleanExisting) styleParts.push(cleanExisting);
      }
      const joinedExisting = styleParts.join('; ');
      if (colorMatch && colorMatch[2] && !/(?:^|;)\s*color\s*:/i.test(joinedExisting)) {
        styleParts.push(`color: ${colorMatch[2].trim()}`);
      }
      if (sizeMatch && sizeMatch[2] && !/font-size\s*:/i.test(joinedExisting)) {
        const mappedSize = fontSizeMap[sizeMatch[2].trim()] || '15px';
        styleParts.push(`font-size: ${mappedSize}`);
      }
      if (!/font-family\s*:/i.test(joinedExisting)) {
        const resolvedFace =
          faceMatch && faceMatch[2]
            ? resolveCompatibleEmailFontStack(faceMatch[2], targetDefaultFont)
            : targetDefaultFont;
        styleParts.push(`font-family: ${resolvedFace}`);
      }
      return `<span style="${styleParts.join('; ')};">${inner}</span>`;
    });
    if (result === prev) break;
  }

  // Helper para limpar e normalizar o conteúdo de atributos style
  const normalizeStyleAttrContent = (rawStyleContent: string): string => {
    let cleanStyle = (rawStyleContent || '').replace(/&quot;|&#39;|&apos;/gi, "'").replace(/"/g, "'");
    cleanStyle = cleanStyle.replace(/;\s*['"]?nasalization['"]?(?:,\s*[^;:]+)*,\s*sans-serif/gi, '');
    if (/font-family\s*:/i.test(cleanStyle)) {
      cleanStyle = cleanStyle.replace(/font-family\s*:\s*([^;]+)/gi, (_m, fontVal: string) => {
        const resolved = resolveCompatibleEmailFontStack(fontVal, targetDefaultFont);
        return `font-family: ${resolved}`;
      });
    }
    return cleanStyle;
  };

  // 3. Normalizar style="..." (aspas duplas)
  result = result.replace(/style="([^"]*)"/gi, (_match, styleContent: string) => {
    return `style="${normalizeStyleAttrContent(styleContent)}"`;
  });

  // 4. Normalizar style='...' (aspas simples)
  result = result.replace(/style='([^']*)'/gi, (_match, styleContent: string) => {
    return `style="${normalizeStyleAttrContent(styleContent)}"`;
  });

  // 5. Garantir que elementos de texto/estrutura têm font-family compatível explícito no inline style
  result = result.replace(
    /<(p|td|th|div|span|strong|b|em|i|u|a|li|h[1-6]|blockquote)(\s[^>]*)?>/gi,
    (fullMatch, tagName: string, rawAttrs?: string) => {
      const attrs = rawAttrs || '';
      const tag = tagName.toLowerCase();
      if (attrs.trim().endsWith('/')) return fullMatch;
      if (/material-icons|mat-icon|fa-|fas\b|far\b|fab\b/i.test(attrs)) return fullMatch;

      const updateStyle = (sc: string): string => {
        let cleanSc = normalizeStyleAttrContent(sc);
        if (!/font-family\s*:/i.test(cleanSc)) {
          const sep = cleanSc.trim() && !cleanSc.trim().endsWith(';') ? '; ' : cleanSc.trim() ? ' ' : '';
          cleanSc = `${cleanSc}${sep}font-family: ${targetDefaultFont};`;
        }
        if (tag === 'p') {
          if (!/(?:^|;)\s*color\s*:/i.test(cleanSc)) {
            const sep = cleanSc.trim() && !cleanSc.trim().endsWith(';') ? '; ' : cleanSc.trim() ? ' ' : '';
            cleanSc = `${cleanSc}${sep}color: #334155;`;
          }
          if (!/font-size\s*:/i.test(cleanSc)) {
            const sep = cleanSc.trim() && !cleanSc.trim().endsWith(';') ? '; ' : cleanSc.trim() ? ' ' : '';
            cleanSc = `${cleanSc}${sep}font-size: 15px; line-height: 1.5;`;
          }
          if (!/(?:^|;)\s*margin(?:-top|-bottom)?\s*:/i.test(cleanSc)) {
            const sep = cleanSc.trim() && !cleanSc.trim().endsWith(';') ? '; ' : cleanSc.trim() ? ' ' : '';
            cleanSc = `${cleanSc}${sep}margin: 0 0 8px 0;`;
          }
        }
        return cleanSc.trim();
      };

      if (/\bstyle\s*=\s*"([^"]*)"/i.test(attrs)) {
        return `<${tagName}${attrs.replace(/\bstyle\s*=\s*"([^"]*)"/i, (_m, sc: string) => `style="${updateStyle(sc)}"`)}>`;
      }
      if (/\bstyle\s*=\s*'([^']*)'/i.test(attrs)) {
        return `<${tagName}${attrs.replace(/\bstyle\s*=\s*'([^']*)'/i, (_m, sc: string) => `style="${updateStyle(sc)}"`)}>`;
      }
      const defaultStyle =
        tag === 'p'
          ? `font-family: ${targetDefaultFont}; font-size: 15px; line-height: 1.5; color: #334155; margin: 0 0 8px 0;`
          : `font-family: ${targetDefaultFont};`;
      return `<${tagName}${attrs} style="${defaultStyle}">`;
    },
  );

  return normalizeEmailHeaderAndStructureHtml(result);
}

/**
 * Garante que um fragmento HTML tem fontes compatíveis com serviços de email e cabeçalho a 100% da largura
 */
export function enforceNasalizationInHtmlFragment(rawHtml: string, preferredFontFamily?: string): string {
  if (!rawHtml || typeof rawHtml !== 'string') return '';

  let html = enforceNasalizationInInlineStyles(rawHtml, preferredFontFamily);
  html = html.replace(/<style[^>]*id=["']sv-nasalization-font["'][^>]*>[\s\S]*?<\/style>/gi, '').trim();
  html = html.replace(/<link[^>]*nasalization\.css[^>]*>/gi, '').trim();

  return html;
}

/**
 * Garante que o HTML completo exportado / salvo / enviado mantém a formatação inline compatível com
 * todos os serviços de email e cabeçalho a 100% da largura.
 */
export function ensureNasalizationInHtml(
  html: string,
  options: { embedFont?: boolean; preferredFontFamily?: string } = {},
): string {
  if (!html || typeof html !== 'string') return '';
  const targetFont = options.preferredFontFamily || DEFAULT_EMAIL_SAFE_FONT_FAMILY;

  let processed = enforceNasalizationInInlineStyles(html, targetFont);

  // Remover folhas de estilo antigas de Nasalization que causavam discrepância entre o editor e o email recebido
  processed = processed.replace(/<link[^>]*href=["']data:text\/css[^"']*["'][^>]*>/gi, '');
  processed = processed.replace(/<link[^>]*nasalization\.css[^>]*>/gi, '');
  processed = processed.replace(/<style[^>]*id=["']sv-nasalization-font["'][^>]*>[\s\S]*?<\/style>/gi, '');

  // Garantir font-family, margin: 0, padding: 0 e fundo branco (ou configurado) no <body> caso exista
  const customOuterBgMatch = processed.match(/\bdata-sv-outer-bg=["']([^"']+)["']/i);
  const customBodyBgMatch = processed.match(/\bdata-sv-body-bg=["']([^"']+)["']/i);
  const targetOuterBg =
    (customOuterBgMatch && customOuterBgMatch[1]) || (customBodyBgMatch && customBodyBgMatch[1]) || '#ffffff';

  processed = processed.replace(/<body([^>]*)>/i, (_fullMatch, attrs: string) => {
    if (/style\s*=/i.test(attrs)) {
      let updatedAttrs = attrs.replace(
        /(background(?:-color)?\s*:\s*)(?:#ecf0f1|#f1f5f9|#f4f4f7|#f4f7f9|#e7e7e7)/gi,
        `$1${targetOuterBg}`,
      );
      if (!/font-family\s*:/i.test(updatedAttrs)) {
        updatedAttrs = updatedAttrs.replace(/style=(["'])/i, `style=$1font-family: ${targetFont}; `);
      }
      if (!/background-color\s*:/i.test(updatedAttrs)) {
        updatedAttrs = updatedAttrs.replace(
          /style=(["'])/i,
          `style=$1margin: 0; padding: 0; background-color: ${targetOuterBg}; `,
        );
      }
      return `<body${updatedAttrs}>`;
    }
    return `<body${attrs} style="margin: 0; padding: 0; background-color: ${targetOuterBg}; font-family: ${targetFont};">`;
  });

  return processed;
}

/**
 * Prepara o HTML para pré-visualização fiel ao que o destinatário recebe nos serviços de email
 */
export function injectNasalizationPreviewStyles(html: string): string {
  if (!html) return html;
  return ensureNasalizationInHtml(html);
}

/**
 * Cria um design Unlayer em branco configurado com fonte compatível com serviços de email
 */
export function createBlankNasalizationDesign(contentWidth: string = '600px'): any {
  const uniqueId = () => 'u_' + Math.random().toString(36).substring(2, 10);
  return {
    counters: { u_column: 1, u_row: 1 },
    body: {
      id: uniqueId(),
      rows: [],
      values: {
        backgroundColor: '#ffffff',
        contentAlign: 'center',
        contentVerticalAlign: 'center',
        contentWidth,
        fontFamily: getNasalizationFontConfig(),
        textColor: '#334155',
        linkStyle: {
          body: true,
          linkColor: '#1b1464',
          linkHoverColor: '#00a896',
          linkUnderline: true,
          linkHoverUnderline: true,
        },
        _meta: { htmlID: uniqueId(), htmlClassNames: 'u_body' },
      },
    },
  };
}

/**
 * Normaliza um design JSON do Unlayer para fontes compatíveis com serviços de email e largura de 600px
 */
export function enforceNasalizationOnDesign(design: any, preferredFontFamily?: string): any {
  if (!design || typeof design !== 'object') {
    return createBlankNasalizationDesign();
  }

  let cloned: any;
  try {
    cloned = JSON.parse(JSON.stringify(design));
  } catch (_) {
    cloned = { ...design };
  }

  if (cloned.design && typeof cloned.design === 'object' && !cloned.body) {
    cloned.design = enforceNasalizationOnDesign(cloned.design, preferredFontFamily);
    if (typeof cloned.html === 'string' && cloned.html.trim()) {
      cloned.html = ensureNasalizationInHtml(cloned.html, { preferredFontFamily });
    }
    return cloned.design;
  }

  const fontConfig = {
    ...getNasalizationFontConfig(),
    ...(preferredFontFamily ? { value: preferredFontFamily } : {}),
  };

  if (!cloned.body) {
    cloned.body = { rows: [], values: {} };
  }
  if (!cloned.body.values) {
    cloned.body.values = {};
  }
  cloned.body.values.contentWidth = '600px';
  const rawDesignBg = String(cloned.body.values.backgroundColor || '')
    .toLowerCase()
    .trim();
  if (
    !rawDesignBg ||
    rawDesignBg === '#ecf0f1' ||
    rawDesignBg === '#f1f5f9' ||
    rawDesignBg === '#f4f4f7' ||
    rawDesignBg === '#f4f7f9' ||
    rawDesignBg === '#e7e7e7'
  ) {
    cloned.body.values.backgroundColor = '#ffffff';
  }
  if (
    preferredFontFamily ||
    !cloned.body.values.fontFamily ||
    /nasalization/i.test(cloned.body.values.fontFamily?.value || '')
  ) {
    cloned.body.values.fontFamily = { ...fontConfig };
  }

  if (Array.isArray(cloned.body.rows)) {
    const totalRows = cloned.body.rows.length;
    cloned.body.rows.forEach((row: any, rowIdx: number) => {
      if (!row || !Array.isArray(row.columns)) return;
      for (const col of row.columns) {
        if (!col || !Array.isArray(col.contents)) continue;
        for (const content of col.contents) {
          if (!content) continue;
          content.values = content.values || {};
          if (content.type === 'image' && (rowIdx === 0 || (totalRows > 1 && rowIdx === totalRows - 1))) {
            content.values.containerPadding = '0px';
            if (content.values.src) {
              content.values.src.fullWidth = true;
              content.values.src.width = '100%';
            }
          }
          if (content.type !== 'divider' && content.type !== 'image' && content.type !== 'social') {
            if (
              preferredFontFamily ||
              !content.values.fontFamily ||
              /nasalization/i.test(content.values.fontFamily?.value || '')
            ) {
              content.values.fontFamily = { ...fontConfig };
            }
          }
          if (typeof content.values.text === 'string') {
            content.values.text = enforceNasalizationInInlineStyles(content.values.text, preferredFontFamily);
          }
          if (content.type === 'html' && typeof content.values.html === 'string') {
            content.values.html = enforceNasalizationInHtmlFragment(content.values.html, preferredFontFamily);
          }
        }
      }
    });
  }

  if (typeof cloned.html === 'string' && cloned.html.trim()) {
    cloned.html = ensureNasalizationInHtml(cloned.html, { preferredFontFamily });
  }

  return cloned;
}
